import * as Yup from "yup";
import { cprMatchesBirthdate } from "./validation";

/**
 * Adgang til CPR-nummeret (ADV-349).
 *
 * Kun brugere med tilladelsen CprRead (Clerk-rollen CprAdgang) må se løbenummeret. Backenden
 * svarer 403 på `GET person/{id}/cpr` for alle andre og afviser i et person-PATCH enhver
 * operation på CprLoebeNr samt ændring af FoedselsDato på en person, der allerede har et
 * CPR-nummer og en fødselsdato (fødselsdatoen er CPR-nummerets første seks cifre). Har
 * personen CPR men ingen fødselsdato, må fødselsdatoen sættes — backenden tjekker, at den
 * svarer til CPR-nummerets datodel.
 */

export const CPR_ADGANG_HINT = "Kræver CPR-adgang";

/** Personens CPR-relevante felter, som afgør hvad en bruger uden CprRead må ændre. */
export interface CprPersonState {
  harCprNummer: boolean;
  /** Personens nuværende (gemte) fødselsdato. */
  foedselsDato: string | Date | null | undefined;
}

/** Fødselsdatoen er låst for brugere uden CprRead, når personen har både CPR og fødselsdato. */
function foedselsDatoErLaast(
  canReadCpr: boolean,
  { harCprNummer, foedselsDato }: CprPersonState,
) {
  return !canReadCpr && harCprNummer && !!foedselsDato;
}

/**
 * Forklaring til brugere uden CprRead i redigeringsformularer, så det ikke ligner en fejl,
 * at CPR-feltet mangler (ADV-349). Returnerer null for brugere med CprRead.
 */
export function cprAdgangInfo(canReadCpr: boolean, person: CprPersonState) {
  if (canReadCpr) return null;
  if (foedselsDatoErLaast(canReadCpr, person)) {
    return "Fødselsdatoen kan ikke ændres, fordi personen har et CPR-nummer. Det kræver CPR-adgang. Kontakt din leder, hvis du har brug for adgang.";
  }
  if (person.harCprNummer) {
    return "Personen har et CPR-nummer, men ingen fødselsdato. Fødselsdatoen kan sættes, men skal svare til CPR-nummerets fødselsdato. CPR-nummeret kræver CPR-adgang.";
  }
  return "CPR-nummer kan kun tilføjes af medarbejdere med CPR-adgang. Kontakt din leder, hvis du har brug for adgang.";
}

/** CPR-nummeret hentes kun, når brugeren har CprRead og der faktisk er bedt om det. */
export function cprQueryEnabled(canReadCpr: boolean, requested: boolean) {
  return canReadCpr && requested;
}

/**
 * Fødselsdatoen må ændres uden CprRead, når personen ikke har et CPR-nummer, eller når
 * personen har CPR men endnu ingen fødselsdato.
 */
export function canEditFoedselsDato(
  canReadCpr: boolean,
  person: CprPersonState,
) {
  return !foedselsDatoErLaast(canReadCpr, person);
}

/**
 * Fjerner de felter fra et person-PATCH-body, som backenden afviser for en bruger uden
 * CprRead. Feltnavne sammenlignes uden hensyn til store/små bogstaver, fordi Stamoplysninger
 * bruger PascalCase (FoedselsDato) og wizards camelCase (foedselsDato).
 */
export function stripCprRestrictedChanges<T extends object>(
  changes: T,
  { canReadCpr, ...person }: { canReadCpr: boolean } & CprPersonState,
): Partial<T> {
  if (canReadCpr) return changes;
  const laast = foedselsDatoErLaast(canReadCpr, person);
  const result: Partial<T> = { ...changes };
  for (const key of Object.keys(result)) {
    const lower = key.toLowerCase();
    if (lower === "cprloebenr" || (lower === "foedselsdato" && laast)) {
      delete result[key as keyof T];
    }
  }
  return result;
}

/**
 * Yup-regel for CPR-løbenummeret i wizards (ADV-349). Med CprRead gælder den hidtidige regel.
 * Uden CprRead vises feltet ikke og sendes aldrig, så det må ikke valideres — ellers afviser
 * reglen den tomme startværdi, og formularen kan ikke indsendes (fejlen ses ikke, fordi
 * feltet er skjult).
 */
export function cprLoebeNrSchema(
  canReadCpr: boolean,
  foedselsDatoField = "foedselsDato",
) {
  if (!canReadCpr) return Yup.string().notRequired();
  return Yup.string()
    .matches(/^[0-9]+$/, "CPR-nummer skal kun indeholde tal")
    .min(4, "CPR-nummer skal være 4 cifre")
    .max(4, "CPR-nummer skal være 4 cifre")
    .test("cpr", "CPR matcher ikke fødselsdato", function (item) {
      return cprMatchesBirthdate(item, this.parent[foedselsDatoField]);
    });
}
